import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  backgroundStreamRegistry,
  BackgroundStreamEntry,
} from '../services/backgroundStreamRegistry';

interface BackgroundStreamContextValue {
  entries: BackgroundStreamEntry[];
  isSessionStreaming: (sessionId: string) => boolean;
  isSessionUnread: (sessionId: string, serverUnread?: boolean) => boolean;
  markSessionRead: (sessionId: string) => void;
}

const BackgroundStreamContext = createContext<BackgroundStreamContextValue | null>(null);

export const BackgroundStreamProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [entries, setEntries] = useState<BackgroundStreamEntry[]>(() =>
    backgroundStreamRegistry.getAllEntries()
  );

  useEffect(() => {
    return backgroundStreamRegistry.subscribe(() => {
      setEntries(backgroundStreamRegistry.getAllEntries());
    });
  }, []);

  const isSessionStreaming = useCallback(
    (sessionId: string) => backgroundStreamRegistry.isSessionStreaming(sessionId),
    []
  );

  const isSessionUnread = useCallback(
    (sessionId: string, serverUnread?: boolean) =>
      backgroundStreamRegistry.isSessionUnread(sessionId) ||
      (serverUnread === true && !backgroundStreamRegistry.isSessionLocallyRead(sessionId)),
    []
  );

  const markSessionRead = useCallback((sessionId: string) => {
    backgroundStreamRegistry.markSessionRead(sessionId);
  }, []);

  return (
    <BackgroundStreamContext.Provider
      value={{ entries, isSessionStreaming, isSessionUnread, markSessionRead }}
    >
      {children}
    </BackgroundStreamContext.Provider>
  );
};

export function useBackgroundStreams(): BackgroundStreamContextValue {
  const ctx = useContext(BackgroundStreamContext);
  if (!ctx) {
    return {
      entries: backgroundStreamRegistry.getAllEntries(),
      isSessionStreaming: (id) => backgroundStreamRegistry.isSessionStreaming(id),
      isSessionUnread: (id, serverUnread) =>
        backgroundStreamRegistry.isSessionUnread(id) ||
        (serverUnread === true && !backgroundStreamRegistry.isSessionLocallyRead(id)),
      markSessionRead: (id) => backgroundStreamRegistry.markSessionRead(id),
    };
  }
  return ctx;
}
